"use client";

import { usePathname } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import {
  findNavIndex,
  isPlainPrimaryClick,
  navItems,
  recordPrimaryNavigation,
  scrollTabRootToTop,
} from "@/components/bottom-navbar/primary-nav";
import { AppImage as Image } from "@/components/ui/app-image";
import Link, { useLinkStatus } from "@/components/ui/link";
import { useAuthState } from "@/hooks/use-auth-state";
import { useTotalUnreadCount } from "@/hooks/use-chat";
import { useShellProfileData } from "@/hooks/use-shell-profile-data";
import { firstMediaPreviewUrl } from "@/lib/media";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";
import { HiiveLogoIcon } from "@/svgs/hiive-logo-icon";

/**
 * `usePathname()` must sit under Suspense (cacheComponents), so the rail reads
 * it through this leaf instead of calling it itself.
 */
function PathnameSync({
  onPathnameChange,
}: {
  onPathnameChange: (pathname: string) => void;
}) {
  const pathname = usePathname();

  useEffect(() => {
    onPathnameChange(pathname);
  }, [onPathnameChange, pathname]);

  return null;
}

function PendingNavigationPulse() {
  const { pending } = useLinkStatus();

  return (
    <span
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 rounded-2xl border border-brand/0 opacity-0",
        pending && "animate-pulse border-brand/35 opacity-100",
      )}
    />
  );
}

/**
 * Primary navigation from `md` up — desktop, tablets, landscape phones and
 * iPhone Duo's inner display — in place of the floating bottom tab bar.
 *
 * Why a rail and not the bar: on a wide window the centred pill leaves the
 * page's whole width to one floating strip, and on iPhone Duo it sits exactly
 * on the fold, where Apple asks for no controls at all. iOS itself moves tab
 * bars to the side there; this is the web view's equivalent.
 *
 * Compact (icon over label) from `md`, a labelled column from `xl`. Its width
 * is `--app-rail-width`, set by AppShell, which pads its content by the same.
 * The rail adds the LEFT safe area itself because it is fixed, outside the
 * body padding that insets everything else (a landscape iPhone's sensor
 * housing, iPhone Duo's side status bar).
 *
 * In iPhone Duo's system column (`side-nav:`, globals.css) it moves into that
 * column, on whichever edge holds it, bottom-aligned under the status bar the
 * way iOS lays out a tab bar there, without the logo or its own surface.
 */
export function AppSideRail() {
  const { authStatus } = useAuthState();
  const { data: profile, isLoading } = useShellProfileData();
  const { data: unreadCount = 0 } = useTotalUnreadCount({
    enabled: authStatus === "authenticated",
  });
  const profileImage = firstMediaPreviewUrl(profile?.images) ?? null;
  // Same hydration rule as the bottom bar: avatar and unread dot come from
  // the persisted query cache after SSR, so they reveal only once mounted.
  const [mounted, setMounted] = useState(false);
  const [pathname, setPathname] = useState<string | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const activeIndex = pathname === null ? null : findNavIndex(pathname);

  return (
    <nav
      aria-label="Primary navigation"
      data-app-side-rail
      className="fixed inset-y-0 left-0 z-40 hidden w-[calc(var(--app-rail-width)+env(safe-area-inset-left,0px))] flex-col border-r border-border bg-background pt-[max(env(safe-area-inset-top,0px),0.75rem)] pb-[max(env(safe-area-inset-bottom,0px),0.75rem)] pl-[env(safe-area-inset-left,0px)] dark:border-white/10 rail:flex side-nav:w-(--side-nav-width) side-nav:justify-end side-nav:border-0 side-nav:bg-transparent side-nav:pl-0 side-nav-right:right-0 side-nav-right:left-auto"
    >
      <Link
        href="/hiives"
        prefetch={false}
        aria-label="Hiive home"
        className="mx-auto mb-4 flex size-12 items-center justify-center rounded-2xl transition-colors xl:mx-3 xl:w-auto xl:justify-start xl:px-3 pointer-fine:hover:bg-surface-alt side-nav:hidden"
      >
        <HiiveLogoIcon width={32} height={32} />
      </Link>

      <ul className="flex flex-col gap-1 px-1.5 xl:px-3 side-nav:short:gap-0">
        {navItems.map((item, index) => {
          const Icon = item.icon;
          const isActive = index === activeIndex;
          const isProfile = item.href === "/profile";
          const hasUnread = mounted && item.href === "/chat" && unreadCount > 0;

          return (
            <li key={item.href}>
              <Link
                href={item.href}
                prefetch={false}
                aria-current={isActive ? "page" : undefined}
                onClick={(event) => {
                  if (
                    isActive &&
                    pathname === item.href &&
                    isPlainPrimaryClick(event)
                  ) {
                    event.preventDefault();
                    uiHaptics.select();
                    recordPrimaryNavigation(item, "side_rail");
                    scrollTabRootToTop(item.href);
                    return;
                  }
                  if (!isActive) uiHaptics.select();
                  recordPrimaryNavigation(item, "side_rail");
                }}
                className={cn(
                  "relative flex min-h-14 flex-col items-center justify-center gap-1 rounded-2xl px-0.5 py-2 transition-colors duration-150 ease-out-strong xl:min-h-12 xl:flex-row xl:justify-start xl:gap-3.5 xl:px-3 side-nav:short:min-h-10 side-nav:short:py-1",
                  isActive
                    ? "bg-surface-alt font-semibold text-text-primary dark:bg-white/10 dark:text-white"
                    : "text-text-secondary dark:text-white/72 pointer-fine:hover:bg-surface-alt pointer-fine:hover:text-text-primary dark:pointer-fine:hover:bg-white/6 dark:pointer-fine:hover:text-white",
                )}
              >
                <PendingNavigationPulse />
                <span className="relative size-6 shrink-0">
                  {isProfile && (!mounted || isLoading) ? (
                    <span className="block size-full animate-pulse rounded-[30%] bg-surface-alt" />
                  ) : isProfile && profileImage ? (
                    <span className="block size-full overflow-hidden rounded-[30%]">
                      <Image
                        src={profileImage}
                        alt=""
                        width={24}
                        height={24}
                        className="size-full object-cover"
                      />
                    </span>
                  ) : (
                    <Icon
                      className="size-full"
                      strokeWidth={isActive ? 2.4 : 2}
                    />
                  )}
                  {hasUnread ? (
                    <span className="absolute -top-0.5 -right-0.5 size-2 rounded-full border border-white bg-accent dark:border-white/20" />
                  ) : null}
                </span>
                {/* Never an ellipsis in the 5rem rail: "Commu…" read as broken
                    on iPhone Duo. Every label fits on one line at this size;
                    a longer one wraps (hyphenated) rather than truncating. */}
                {/* Icons alone in a short system column (Duo closed and
                    rotated): five labelled tabs plus the header's buttons
                    would not fit under its status bar. */}
                <span className="max-w-full text-center text-[10px] leading-tight font-semibold text-balance break-words hyphens-auto xl:truncate xl:text-left xl:text-[15px] xl:font-medium side-nav:short:sr-only">
                  {item.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      <Suspense fallback={null}>
        <PathnameSync onPathnameChange={setPathname} />
      </Suspense>
    </nav>
  );
}
